/**
 * Minimal Excel (.xlsx) reader: cell values from one worksheet, in the browser.
 * Numbers stay numbers, so date cells arrive as Excel serial numbers, which the
 * engine's parseDate understands. Formulas give their cached value.
 */
import JSZip from "jszip";

export interface Sheet {
  headers: string[];
  rows: Record<string, string | number>[];
}

const ENTITY: Record<string, string> = { amp: "&", lt: "<", gt: ">", quot: '"', apos: "'" };

export function decodeXml(s: string): string {
  return s.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (m, e: string) => {
    if (e[0] === "#") {
      const code = e[1] === "x" || e[1] === "X" ? parseInt(e.slice(2), 16) : parseInt(e.slice(1), 10);
      return Number.isFinite(code) ? String.fromCodePoint(code) : m;
    }
    return ENTITY[e.toLowerCase()] ?? m;
  });
}

/** Concatenate every <t> run, ignoring phonetic hints (<rPh>). */
function textRuns(xml: string): string {
  const noPhonetic = xml.replace(/<rPh\b[\s\S]*?<\/rPh>/g, "");
  let out = "";
  for (const m of noPhonetic.matchAll(/<t(?:\s[^>]*)?>([\s\S]*?)<\/t>/g)) out += decodeXml(m[1] ?? "");
  return out;
}

export function columnIndex(ref: string): number {
  const letters = /^[A-Z]+/.exec(ref)?.[0] ?? "A";
  let n = 0;
  for (const ch of letters) n = n * 26 + (ch.charCodeAt(0) - 64);
  return n - 1;
}

function attr(attrs: string, name: string): string | undefined {
  return new RegExp(`\\b${name}="([^"]*)"`).exec(attrs)?.[1];
}

function resolveTarget(target: string): string {
  if (target.startsWith("/")) return target.slice(1);
  return `xl/${target}`.replace(/\/\.\//g, "/");
}

export class Workbook {
  private zip: JSZip;
  private shared: string[];
  private paths: Map<string, string>;
  readonly sheetNames: string[];

  private constructor(zip: JSZip, shared: string[], sheetNames: string[], paths: Map<string, string>) {
    this.zip = zip;
    this.shared = shared;
    this.sheetNames = sheetNames;
    this.paths = paths;
  }

  static async open(data: ArrayBuffer | Uint8Array): Promise<Workbook> {
    const zip = await JSZip.loadAsync(data);
    const read = async (p: string) => (await zip.file(p)?.async("string")) ?? "";
    const wb = await read("xl/workbook.xml");
    if (!wb) throw new Error("Not an Excel workbook (.xlsx)");
    const rels = await read("xl/_rels/workbook.xml.rels");
    const relTarget = new Map<string, string>();
    for (const m of rels.matchAll(/<Relationship\b([^>]*)\/?>/g)) {
      const id = attr(m[1]!, "Id");
      const target = attr(m[1]!, "Target");
      if (id && target) relTarget.set(id, resolveTarget(target));
    }
    const names: string[] = [];
    const paths = new Map<string, string>();
    for (const m of wb.matchAll(/<sheet\b([^>]*)\/?>/g)) {
      const name = decodeXml(attr(m[1]!, "name") ?? "");
      const rid = attr(m[1]!, "r:id");
      const path = rid ? relTarget.get(rid) : undefined;
      if (name && path) {
        names.push(name);
        paths.set(name, path);
      }
    }
    const sst = await read("xl/sharedStrings.xml");
    const shared = [...sst.matchAll(/<si>([\s\S]*?)<\/si>/g)].map((m) => textRuns(m[1] ?? ""));
    return new Workbook(zip, shared, names, paths);
  }

  /** Read a sheet; the first non-empty row is the header row. */
  async sheet(name: string = this.sheetNames[0] ?? ""): Promise<Sheet> {
    const path = this.paths.get(name);
    if (!path) throw new Error(`Sheet "${name}" not found`);
    const xml = (await this.zip.file(path)?.async("string")) ?? "";
    const grid: (string | number)[][] = [];
    for (const rm of xml.matchAll(/<row\b[^>]*?(?:\/>|>([\s\S]*?)<\/row>)/g)) {
      const cells: (string | number)[] = [];
      let next = 0;
      for (const cm of (rm[1] ?? "").matchAll(/<c\b([^>]*?)(?:\/>|>([\s\S]*?)<\/c>)/g)) {
        const attrs = cm[1] ?? "";
        const body = cm[2] ?? "";
        const ref = attr(attrs, "r");
        const col = ref ? columnIndex(ref) : next;
        next = col + 1;
        const type = attr(attrs, "t") ?? "n";
        const v = /<v>([\s\S]*?)<\/v>/.exec(body)?.[1];
        let value: string | number = "";
        if (type === "s") value = this.shared[Number(v)] ?? "";
        else if (type === "inlineStr") value = textRuns(body);
        else if (type === "str" || type === "e") value = decodeXml(v ?? "");
        else if (type === "b") value = v === "1" ? "TRUE" : "FALSE";
        else if (v !== undefined && v !== "") value = Number(v);
        cells[col] = value;
      }
      grid.push(Array.from(cells, (c) => c ?? ""));
    }
    const nonEmpty = grid.filter((r) => r.some((c) => String(c).trim() !== ""));
    const head = nonEmpty[0] ?? [];
    const seen = new Map<string, number>();
    const headers = head.map((h, i) => {
      const base = String(h).trim() || `Column ${i + 1}`;
      const n = (seen.get(base) ?? 0) + 1;
      seen.set(base, n);
      return n === 1 ? base : `${base} (${n})`;
    });
    const rows = nonEmpty.slice(1).map((r) => {
      const o: Record<string, string | number> = {};
      headers.forEach((h, i) => (o[h] = r[i] ?? ""));
      return o;
    });
    return { headers, rows };
  }
}
